import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Options for AI generation with async generator pattern.
 */
export interface UseAIGenerationWithStreamOptions {
  /** Called when generation completes successfully */
  onComplete?: (text: string) => void;
  /** Called when generation fails */
  onError?: (error: Error) => void;
  /** Called when text updates during streaming */
  onTextUpdate?: (text: string) => void;
  /** Post-process the accumulated text on every chunk (e.g. strip code fences) */
  transformText?: (text: string) => string;
}

/**
 * Return type for useAIGenerationWithStream hook.
 */
export interface UseAIGenerationWithStreamReturn {
  /** The current generated text (accumulates as streaming progresses) */
  generatedText: string;
  /** Whether generation is currently in progress */
  isGenerating: boolean;
  /** Error message if generation failed */
  error: string | null;
  /** Start generating content with the given async generator */
  generate: (
    generator: (signal: AbortSignal) => AsyncGenerator<string>,
  ) => Promise<void>;
  /** Regenerate with feedback and previous generation */
  regenerate: (
    generator: (
      currentValue: string,
      feedback?: string,
      previousGeneration?: string,
      signal?: AbortSignal,
    ) => AsyncGenerator<string>,
    currentValue: string,
    feedback: string,
    previousGeneration: string,
  ) => Promise<void>;
  /** Cancel an ongoing generation */
  cancel: () => void;
  /** Reset all state */
  reset: () => void;
}

/**
 * Hook for handling AI generation with async generator pattern.
 * Manages state, abort controllers, and streaming logic.
 */
export function useAIGenerationWithStream(
  options: UseAIGenerationWithStreamOptions = {},
): UseAIGenerationWithStreamReturn {
  const { onComplete, onError, onTextUpdate, transformText } = options;

  const [generatedText, setGeneratedText] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);
  const previousGenerationRef = useRef<string | null>(null);

  const reset = useCallback(() => {
    setGeneratedText("");
    setIsGenerating(false);
    setError(null);
    previousGenerationRef.current = null;
  }, []);

  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsGenerating(false);
  }, []);

  const generate = useCallback(
    async (generator: (signal: AbortSignal) => AsyncGenerator<string>) => {
      // Cancel any existing generation
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }

      // Reset state
      setGeneratedText("");
      setError(null);
      setIsGenerating(true);

      // Create new abort controller
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        let fullText = "";

        for await (const chunk of generator(abortController.signal)) {
          if (abortController.signal.aborted) {
            break;
          }
          fullText += chunk;
          const displayText = transformText
            ? transformText(fullText)
            : fullText;
          setGeneratedText(displayText);
          onTextUpdate?.(displayText);
        }

        // Store successful generation for potential feedback
        const finalText = transformText ? transformText(fullText) : fullText;
        previousGenerationRef.current = finalText;

        setIsGenerating(false);
        onComplete?.(finalText);
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          // Generation was cancelled, don't treat as error
          return;
        }

        const errorMessage =
          err instanceof Error ? err.message : "Generation failed";
        setError(errorMessage);
        setIsGenerating(false);
        onError?.(err instanceof Error ? err : new Error(errorMessage));
      } finally {
        if (abortControllerRef.current === abortController) {
          abortControllerRef.current = null;
        }
      }
    },
    [onComplete, onError, onTextUpdate, transformText],
  );

  const regenerate = useCallback(
    async (
      generator: (
        currentValue: string,
        feedback?: string,
        previousGeneration?: string,
        signal?: AbortSignal,
      ) => AsyncGenerator<string>,
      currentValue: string,
      feedback: string,
      previousGeneration: string,
    ) => {
      await generate((signal) =>
        generator(currentValue, feedback, previousGeneration, signal),
      );
    },
    [generate],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);

  return {
    generatedText,
    isGenerating,
    error,
    generate,
    regenerate,
    cancel,
    reset,
  };
}
